import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ThemeContext } from "./context/ThemeContext";
import { useSelector, useDispatch } from "react-redux";
import { persistor } from "./redux";
import { userLogout } from "./sagas/actions";
import Header from "./components/Common/Header";
import Footer from "./components/Common/Footer";
import LandingPage from "./pages/LandingPage/LandingPage";
import GlobalErrorModal from "./components/Common/GlobalErrorModal";
import SubscriptionInfo from "./components/Common/SubscriptionInfo";
import ZoomOut from "./components/Common/ZoomOut";
import ZoomOutUser from "./components/Common/ZoomOutUser";
import EdBrowserChatbot from "./components/ISAFEAI/EdbrowserChatbot";
import ForgetPasswordPage from "./pages/LandingPage/ForgetPasswordPage";
import RegistrationPage from "./pages/LandingPage/RegistrationPage";
import ClassLinkCallbackPage from "./pages/LandingPage/ClassLinkCallbackPage";
import MicrosoftCallbackPage from "./pages/LandingPage/MicrosoftCallbackPage";
import DashboardPage from "./pages/DashboardPage/DashboardPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import AdminUserProfilePage from "./pages/AdminCentral/UserProfile/AdminUserProfilePage";
import VideosPage from "./pages/VideosPage/VideosPage";
import ILEARNProgressPage from "./pages/ILEARNProgressPage/ILEARNProgressPage";
import DashboardMenu from "./components/Dashboard/DashboardMenu";
import AdminCentralDashboardMenu from "./components/AdminCentral/Dashboard/AdminCentralDashboardMenu";
import Greeting from "./components/Dashboard/Greeting";
import LoginSuccessBanner from "./components/Dashboard/LoginSuccessBanner";
import ILEARNModulesPage from "./pages/ILEARNProgressPage/ILEARNModulesPage";
import ILEARNModule1Page from "./pages/ILEARNProgressPage/ILEARNModule1";
import ILEARNModule2Page from "./pages/ILEARNProgressPage/ILEARNModule2";
import ILEARNModule3Page from "./pages/ILEARNProgressPage/ILEARNModule3";
import ILEARNModule4Page from "./pages/ILEARNProgressPage/ILEARNModule4";
import ILEARNModule5Page from "./pages/ILEARNProgressPage/ILEARNModule5";
import ILEARNModule6Page from "./pages/ILEARNProgressPage/ILEARNModule6";
import ILEARNModule7Page from "./pages/ILEARNProgressPage/ILEARNModule7";
import CertificatePage from "./pages/CertificatePage/CertificatePage";
import CCSSPage from "./pages/MyInfo/CCSSPage";
import SubscriptionAdminDashboardPage from "./pages/AdminCentral/DashboardPage/SubscriptionAdminCentralDashboardPage";
import AdminCentralDashboardPage from "./pages/AdminCentral/DashboardPage/AdminCentralDashboardPage";
import AdminAnnouncementsPage from "./pages/AdminCentral/Announcements/AdminCentralAnnouncementPage";
import SubscriptionsPage from "./pages/AdminCentral/Subscriptions/SubscriptionsPage";
import SubscriptionAdminsPage from "./pages/AdminCentral/Admins/SubscriptionAdminsDashboard";
import AdminsPage from "./pages/AdminCentral/Admins/AdminsDashboardPage";
import TeachersPage from "./pages/AdminCentral/Teachers/TeachersPage";
import DirectoryPage from "./pages/AdminCentral/Directory/DirectoryPage";
import OtherPage from "./pages/AdminCentral/Other/OtherPage";
import ReportPage from "./pages/AdminCentral/Reports/ReportPage";
import ResourcesPage from "./pages/AdminCentral/Resources/ResourcesPage";
import UserGuidePage from "./pages/AdminCentral/UserGuide/UserGuidePage";
import ResetPassword from "./components/Login/ResetPassword";
import withColors from "./util/withColors";
import styled, { keyframes } from "styled-components";
import API, { ensureFreshToken } from "./util/api";
import { Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const breakpoints = {
  tablet: 768,
  phone: 480,
};

const SESSION_VALIDATION_TTL_MS = 30 * 1000;

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

const WrappedLandingPage = withColors(LandingPage);
const WrappedForgetPasswordPage = withColors(ForgetPasswordPage);
const WrappedRegistrationPage = withColors(RegistrationPage);
const WrappedClassLinkCallbackPage = withColors(ClassLinkCallbackPage);
const WrappedMicrosoftCallbackPage = withColors(MicrosoftCallbackPage);
const WrappedDashboardPage = withColors(DashboardPage);
const WrappedProfilePage = withColors(ProfilePage);
const WrappedAdminUserProfilePage = withColors(AdminUserProfilePage);
const WrappedVideosPage = withColors(VideosPage);
const WrappedISAFEAdminDashboardPage = withColors(AdminCentralDashboardPage);
const WrappedAdminAnnouncementsPage = withColors(AdminAnnouncementsPage);
const WrappedAdminSubscriptionsPage = withColors(SubscriptionsPage);
const WrappedAdminsPage = withColors(AdminsPage);
const WrappedDirectoryPage = withColors(DirectoryPage);
const WrappedOtherPage = withColors(OtherPage);
const WrappedTeachersPage = withColors(TeachersPage);
const WrappedSubscriptionAdminPage = withColors(SubscriptionAdminsPage);
const WrappedUserGuidePage = withColors(UserGuidePage);
const WrappedResourcesPage = withColors(ResourcesPage);
const WrappedReportsPage = withColors(ReportPage);
const WrappedResetPasswordPage = withColors(ResetPassword);
const WrappedILEARNProgressPage = withColors(ILEARNProgressPage);
const WrappedILEARNModulesPage = withColors(ILEARNModulesPage);
const WrappedILEARNModule1Page = withColors(ILEARNModule1Page);
const WrappedILEARNModule2Page = withColors(ILEARNModule2Page);
const WrappedILEARNModule3Page = withColors(ILEARNModule3Page);
const WrappedILEARNModule4Page = withColors(ILEARNModule4Page);
const WrappedILEARNModule5Page = withColors(ILEARNModule5Page);
const WrappedILEARNModule6Page = withColors(ILEARNModule6Page);
const WrappedILEARNModule7Page = withColors(ILEARNModule7Page);
const WrappedCertificatePage = withColors(CertificatePage);
const WrappedCCSSPage = withColors(CCSSPage);

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 100vh;
  width: 100%;
  background-color: white;

  @media (max-width: ${breakpoints.tablet}px) {
    align-items: stretch;
  }
`;

const ContentContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["isLandingPage", "loginBackground", "backgroundImage"].includes(prop),
})`
  width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${({ isLandingPage, loginBackground, backgroundImage }) =>
      `url(${isLandingPage ? loginBackground : backgroundImage})`}
    no-repeat center center;
  background-size: cover;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  overflow: auto;
  display: flex;
  flex-direction: column;
  @media (max-width: 1024px) {
    width: 100%;
  }
  @media (max-width: ${breakpoints.tablet}px) {
    width: 100%;
    box-shadow: none;
    background-position: top;
  }
`;

const RoutesWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 20px 20px;
`;

const spinnerRotation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const GlobalLoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(4, 15, 29, 0.75);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const GlobalLoadingCard = styled.div`
  background: rgba(15, 34, 58, 0.95);
  color: #fff;
  padding: 32px 40px;
  border-radius: 12px;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 20px 45px rgba(3, 8, 20, 0.45);
`;

const GlobalLoadingSpinner = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.25);
  border-top-color: #fff;
  animation: ${spinnerRotation} 0.9s linear infinite;
`;

const GlobalLoadingMessage = styled.p`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
`;

const IncidentBannerWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "colors",
})`
  width: 100%;
  background: ${({ colors }) => colors?.incidentBannerBackground || "#FCD34D"};
  color: ${({ colors }) => colors?.incidentBannerText || "#1A1A1A"};
  display: flex;
  justify-content: center;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 140;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const IncidentBannerContent = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
`;

const BannerTextGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: center;

  @media (max-width: ${breakpoints.tablet}px) {
    flex-wrap: wrap;
    text-align: center;
    gap: 6px;
  }
`;

const BannerMessage = styled.span`
  flex: 1;
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.02em;
  text-align: center;

  @media (max-width: ${breakpoints.tablet}px) {
    flex-basis: 100%;
    text-align: center;
  }
`;

const BannerMessageGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0;
`;

const BannerNavButton = styled.button`
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(26, 26, 26, 0.15);
  color: #1a1a1a;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.85);
    border-color: rgba(26, 26, 26, 0.25);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(26, 26, 26, 0.25);
  }

  @media (max-width: ${breakpoints.tablet}px) {
    width: 28px;
    height: 28px;
  }
`;

const InfoIconButton = styled.button`
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(26, 26, 26, 0.15);
  color: #1a1a1a;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.85);
    border-color: rgba(26, 26, 26, 0.25);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(26, 26, 26, 0.25);
  }

  @media (max-width: ${breakpoints.tablet}px) {
    width: 28px;
    height: 28px;
  }
`;

const BannerCounter = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.15);

  @media (max-width: ${breakpoints.tablet}px) {
    order: -1;
  }
`;

const ISAFE_ROLES = new Set([
  "isafe_accountant",
  "isafe_moderator",
  "isafe_salesperson",
  "isafe_admin",
  "root",
]);
const SUBSCRIPTION_ROLES = new Set([
  "subscription_access",
  "subscription_accountant",
  "subscription_admin",
  "subscription_implementor",
  "subscription_salesperson",
]);
const TEACHER_ROLE = "subscription_teacher";

// ProtectedRoute Component
const ProtectedRoute = ({ element }) => {
  const signedIn = useSelector((state) => state.auth.signedIn);
  const location = useLocation();

  if (!signedIn) {
    // Redirect to login if not authenticated
    return <Navigate to="/user/login" state={{ from: location }} />;
  }

  return element;
};

// PublicRoute Component
const PublicRoute = ({ element }) => {
  const signedIn = useSelector((state) => state.auth.signedIn);
  const location = useLocation();
  const isPasswordResetRoute = location.pathname.startsWith("/password/reset/");

  if (signedIn && !isPasswordResetRoute) {
    // Redirect to dashboard if already signed in (except for password reset)
    return <Navigate to="/myinfo" />;
  }

  return element;
};

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authValidationInFlightRef = useRef(false);
  const { theme } = useContext(ThemeContext);
  const colors = theme.colors;
  const backgroundImage = colors.backgroundImage;
  const loginBackgroundImage = colors.loginBackgroundImage;
  const signedIn = useSelector((state) => state.auth.signedIn);
  const user = useSelector((state) => state.auth.user);
  const userName = user?.first_name;
  const location = useLocation();
  const normalizedPath =
    location.pathname.length > 1 && location.pathname.endsWith("/")
      ? location.pathname.slice(0, -1)
      : location.pathname;
  const isLandingPage = normalizedPath.startsWith("/user/login");
  const isAdminPage = normalizedPath.includes("admincentral");
  const become = useSelector((state) => state.auth.become);
  const become_user = useSelector((state) => state.auth.become_user);
  const become_origin = useSelector((state) => state.auth.become_origin);
  const roles = useSelector((state) => state.auth.user.roles);
  const globalLoading = useSelector((state) => state.ui.globalLoading);
  const roleIDs = new Set(roles?.map((r) => r.id));
  const hasSubscriptionAdminRole = Array.from(SUBSCRIPTION_ROLES).some((r) =>
    roleIDs.has(r),
  );
  const hasISAFEAdminRole = Array.from(ISAFE_ROLES).some((r) => roleIDs.has(r));
  const selfImplementationNotice = useSelector(
    (state) => state.auth.self_implementation_notice,
  );
  const [showLoginBanner, setShowLoginBanner] = useState(false);
  const [selfImplementationBanner, setSelfImplementationBanner] =
    useState(null);
  const [incidents, setIncidents] = useState([]);
  const [currentIncidentIndex, setCurrentIncidentIndex] = useState(0);
  const [incidentFetchAttempted, setIncidentFetchAttempted] = useState(false);
  const loginBannerTimerRef = useRef(null);
  const latestSelfImplementationTimestamp = useRef(null);
  const previousSignedIn = useRef(signedIn);
  const token = useSelector((state) => state.auth.token);
  const loginResponse = useSelector((state) => state.auth.loginResponse);
  const sessionValidatedAt = useSelector(
    (state) => state.auth.session_validated_at,
  );
  const showSubscriptionInfo =
    signedIn &&
    ((hasISAFEAdminRole && become == 1) ||
      (!hasISAFEAdminRole && hasSubscriptionAdminRole));
  const showZoomOut = signedIn && become == 1 && hasISAFEAdminRole;
  const showZoomOutUser = signedIn && become_user == 1;
  const prevBecomeUser = usePrevious(become_user);
  const prevBecomeOrigin = usePrevious(become_origin);

  useEffect(() => {
    if (prevBecomeUser === 1 && become_user === 0) {
      if (prevBecomeOrigin === "directory") {
        navigate("/admincentral/orgs/search", {
          state: { initialTab: "User Search" },
        });
      } else {
        navigate("/admincentral/teachers");
      }
    }
  }, [become_user, prevBecomeUser, prevBecomeOrigin, navigate]);

  useEffect(() => {
    const validateOrRefresh = async () => {
      // Skip auth validation for public routes or when not signed in
      const publicRoutes = new Set(["/password/request", "/register"]);
      const isPasswordResetRoute =
        normalizedPath.startsWith("/password/reset/");

      if (!signedIn) {
        // Do not attempt token validation on public pages when unauthenticated
        if (
          isLandingPage ||
          publicRoutes.has(normalizedPath) ||
          isPasswordResetRoute
        ) {
          return;
        }
        return;
      }

      if (!user?.user_id) {
        // Wait for profile information before attempting validation
        return;
      }

      const now = Date.now();
      const sessionFresh =
        sessionValidatedAt &&
        now - sessionValidatedAt < SESSION_VALIDATION_TTL_MS;

      if (sessionFresh) {
        return;
      }

      if (authValidationInFlightRef.current) return;
      authValidationInFlightRef.current = true;
      try {
        // 1) First, try validating the current access token
        const validation = await API.getUserProfile({ user_id: user.user_id });
        if (validation.status === "Success") {
          // Only touch the session timestamp — do NOT overwrite loginResponse
          // with the stale closure value. If ensureFreshToken() ran concurrently
          // and rotated the refresh_token in Redux, we must not clobber it.
          dispatch({ type: "TOUCH_SESSION" });
          return;
        }
        // else: our access token is invalid/expired
        throw new Error("access_token_invalid");
      } catch (e) {
        console.warn("Access token invalid, attempting refresh…", e);

        try {
          // 2) Attempt to refresh using the shared ensureFreshToken() lock so
          //    this path and the saga's background refresh never consume the
          //    same rotating refresh token simultaneously.
          //    ensureFreshToken() dispatches UPDATE_ACCESS_TOKEN (with expiryTime)
          //    internally, so no separate dispatch is needed here.
          await ensureFreshToken();
          if (isLandingPage) {
            navigate("/myinfo");
          }
          return;
        } catch (refreshError) {
          console.error("Refresh token failed:", refreshError);
          persistor.purge();
          dispatch(userLogout());
          // Avoid forcing navigation away from public routes
          if (
            !isLandingPage &&
            !publicRoutes.has(normalizedPath) &&
            !isPasswordResetRoute
          ) {
            navigate("/user/login");
          }
        }
      } finally {
        authValidationInFlightRef.current = false;
      }
    };
    validateOrRefresh();
  }, [
    normalizedPath,
    isLandingPage,
    signedIn,
    user?.user_id,
    dispatch,
    navigate,
    sessionValidatedAt,
  ]);

  useEffect(() => {
    if (incidentFetchAttempted || !token) {
      return;
    }

    const fetchIncidents = async () => {
      try {
        const response = await API.getIncidentReport({});
        const activeIncidents = Array.isArray(response?.incidents)
          ? response.incidents.filter(
              (incidentItem) =>
                incidentItem &&
                (incidentItem.is_active === "1" ||
                  incidentItem.is_active === 1 ||
                  incidentItem.is_active === true),
            )
          : [];

        if (activeIncidents.length > 0) {
          setIncidents(activeIncidents);
          setCurrentIncidentIndex(0);
        } else {
          setIncidents([]);
          setCurrentIncidentIndex(0);
        }
      } catch (incidentError) {
        console.error("Unable to retrieve incidents:", incidentError);
      } finally {
        setIncidentFetchAttempted(true);
      }
    };

    fetchIncidents();
  }, [incidentFetchAttempted, token]);

  useEffect(() => {
    if (incidents.length <= 1) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setCurrentIncidentIndex(
        (prevIndex) => (prevIndex + 1) % incidents.length,
      );
    }, 8000);

    return () => clearInterval(intervalId);
  }, [incidents]);

  const handleNextIncident = () => {
    setCurrentIncidentIndex((prevIndex) => {
      const totalIncidents = incidents.length;
      if (totalIncidents === 0) {
        return 0;
      }

      return (prevIndex + 1) % totalIncidents;
    });
  };

  const handlePrevIncident = () => {
    setCurrentIncidentIndex((prevIndex) => {
      const totalIncidents = incidents.length;
      if (totalIncidents === 0) {
        return 0;
      }

      return (prevIndex - 1 + totalIncidents) % totalIncidents;
    });
  };

  useEffect(() => {
    const wasSignedIn = previousSignedIn.current;

    if (signedIn && !wasSignedIn) {
      setShowLoginBanner(true);
      if (loginBannerTimerRef.current) {
        clearTimeout(loginBannerTimerRef.current);
      }
      loginBannerTimerRef.current = setTimeout(() => {
        setShowLoginBanner(false);
        loginBannerTimerRef.current = null;
      }, 40000);
    }

    if (!signedIn && wasSignedIn) {
      setShowLoginBanner(false);
      if (loginBannerTimerRef.current) {
        clearTimeout(loginBannerTimerRef.current);
        loginBannerTimerRef.current = null;
      }
    }

    previousSignedIn.current = signedIn;
  }, [signedIn]);

  useEffect(
    () => () => {
      if (loginBannerTimerRef.current) {
        clearTimeout(loginBannerTimerRef.current);
        loginBannerTimerRef.current = null;
      }
    },
    [],
  );

  const handleDismissLoginBanner = () => {
    setShowLoginBanner(false);
    if (loginBannerTimerRef.current) {
      clearTimeout(loginBannerTimerRef.current);
      loginBannerTimerRef.current = null;
    }
  };

  const handleDismissSelfImplementationBanner = () => {
    setSelfImplementationBanner(null);
  };

  useEffect(() => {
    if (!selfImplementationNotice) return;
    const { status, message, timestamp } = selfImplementationNotice;
    if (!timestamp || latestSelfImplementationTimestamp.current === timestamp) {
      return;
    }

    latestSelfImplementationTimestamp.current = timestamp;

    let bannerMessage = null;
    if (status === "set") {
      bannerMessage = "Your self implementation is Successfully set";
    } else if (status === "unset") {
      bannerMessage = "Your self implementation is Successfully unset";
    } else if (status === "error") {
      bannerMessage =
        message || "Unable to update self implementation. Please try again.";
    }

    if (!bannerMessage) return;

    setSelfImplementationBanner(bannerMessage);
  }, [selfImplementationNotice]);

  useEffect(() => {
    if (!selfImplementationBanner) return undefined;
    const timer = setTimeout(() => {
      setSelfImplementationBanner(null);
    }, 3000);
    return () => clearTimeout(timer);
  }, [selfImplementationBanner]);

  if (isAdminPage) {
    for (let isafeRole of ISAFE_ROLES) {
      if (roleIDs.has(isafeRole)) {
        break;
      }
    }
    const hasISAFERole = Array.from(ISAFE_ROLES).some((r) => roleIDs.has(r));
    if (!hasISAFERole) {
      const hasSubscriptionRole = Array.from(SUBSCRIPTION_ROLES).some((r) =>
        roleIDs.has(r),
      );
      if (hasSubscriptionRole) {
        if (
          roleIDs.has("subscription_admin") &&
          normalizedPath.startsWith("/admincentral/isafeadmins")
        ) {
          return <Navigate to="/admincentral/dashboard" replace />;
        }
      } else {
        if (roleIDs.has(TEACHER_ROLE)) {
          return <Navigate to="/myinfo" replace />;
        }
        return <Navigate to="/not-authorized" replace />;
      }
    }
  }

  const incidentCount = incidents.length;
  const hasIncidents = incidentCount > 0;
  const activeIncident = hasIncidents ? incidents[currentIncidentIndex] : null;
  const hasMultipleIncidents = incidentCount > 1;
  const incidentDescription = activeIncident?.description
    ? activeIncident.description.replace(/\r?\n/g, "\n").trim()
    : "";
  const incidentBanner = hasIncidents ? (
    <IncidentBannerWrapper colors={colors}>
      <IncidentBannerContent>
        {hasMultipleIncidents && (
          <BannerNavButton
            type="button"
            onClick={handlePrevIncident}
            aria-label="View previous incident"
          >
            <FiChevronLeft size={18} />
          </BannerNavButton>
        )}
        <BannerTextGroup>
          {hasMultipleIncidents && (
            <BannerCounter>
              {currentIncidentIndex + 1}/{incidentCount}
            </BannerCounter>
          )}
          <BannerMessageGroup>
            {incidentDescription && (
              <Tooltip
                arrow
                placement="bottom-start"
                title={
                  <span style={{ whiteSpace: "pre-line" }}>
                    {incidentDescription}
                  </span>
                }
                enterTouchDelay={0}
                leaveTouchDelay={4000}
              >
                <InfoIconButton
                  type="button"
                  aria-label="View incident details"
                >
                  <InfoOutlinedIcon fontSize="small" />
                </InfoIconButton>
              </Tooltip>
            )}
            <BannerMessage role="status" aria-live="polite" aria-atomic="true">
              {activeIncident?.incident || ""}
            </BannerMessage>
          </BannerMessageGroup>
        </BannerTextGroup>
        {hasMultipleIncidents && (
          <BannerNavButton
            type="button"
            onClick={handleNextIncident}
            aria-label="View next incident"
          >
            <FiChevronRight size={18} />
          </BannerNavButton>
        )}
      </IncidentBannerContent>
    </IncidentBannerWrapper>
  ) : null;

  return (
    <AppContainer>
      <GlobalErrorModal />
      {globalLoading?.show && (
        <GlobalLoadingOverlay
          role="status"
          aria-live="assertive"
          aria-busy="true"
        >
          <GlobalLoadingCard>
            <GlobalLoadingSpinner />
            <GlobalLoadingMessage>
              {globalLoading.message || "Loading..."}
            </GlobalLoadingMessage>
          </GlobalLoadingCard>
        </GlobalLoadingOverlay>
      )}
      {incidentBanner}
      <ContentContainer
        backgroundImage={backgroundImage}
        loginBackground={loginBackgroundImage}
        isLandingPage={isLandingPage}
      >
        {showZoomOut && <ZoomOut colors={colors} />}
        {showZoomOutUser && <ZoomOutUser colors={colors} />}
        {showSubscriptionInfo && <SubscriptionInfo colors={colors} />}

        <Header colors={colors} />
        {selfImplementationBanner && (
          <LoginSuccessBanner
            colors={colors}
            message={selfImplementationBanner}
            onDismiss={handleDismissSelfImplementationBanner}
          />
        )}
        {signedIn && !isAdminPage && (
          <>
            {showLoginBanner && (
              <LoginSuccessBanner
                colors={colors}
                onDismiss={handleDismissLoginBanner}
              />
            )}
            <Greeting username={userName} colors={colors} />
            <DashboardMenu />
          </>
        )}
        {signedIn && isAdminPage && (
          <>
            <AdminCentralDashboardMenu colors={colors} />
          </>
        )}
        <RoutesWrapper>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/user/login/osceola"
              element={
                <PublicRoute
                  element={<WrappedLandingPage initialOtpVerified />}
                />
              }
            />
            <Route
              path="/user/login/alt"
              element={
                <PublicRoute
                  element={<WrappedLandingPage initialOtpVerified />}
                />
              }
            />
            <Route
              path="/user/login"
              element={<PublicRoute element={<WrappedLandingPage />} />}
            />
            <Route
              path="/password/request"
              element={<PublicRoute element={<WrappedForgetPasswordPage />} />}
            />
            <Route
              path="/register"
              element={<PublicRoute element={<WrappedRegistrationPage />} />}
            />
            <Route
              path="/sso/classlink/callback"
              element={
                <PublicRoute element={<WrappedClassLinkCallbackPage />} />
              }
            />
            <Route
              path="/sso/microsoft/callback"
              element={
                <PublicRoute element={<WrappedMicrosoftCallbackPage />} />
              }
            />
            <Route
              path="/password/reset/:code"
              element={<PublicRoute element={<WrappedResetPasswordPage />} />}
            />

            {/* Protected Routes */}
            <Route
              path="/myinfo"
              element={<ProtectedRoute element={<WrappedDashboardPage />} />}
            />
            <Route
              path="/user/update"
              element={<ProtectedRoute element={<WrappedProfilePage />} />}
            />
            <Route
              path="/admincentral/users/update/:userId"
              element={
                <ProtectedRoute element={<WrappedAdminUserProfilePage />} />
              }
            />
            <Route
              path="/myinfo/videos"
              element={<ProtectedRoute element={<WrappedVideosPage />} />}
            />
            <Route
              path="/myinfo/moduleprogress"
              element={
                <ProtectedRoute element={<WrappedILEARNProgressPage />} />
              }
            />
            <Route
              path="/myinfo/ccss"
              element={<ProtectedRoute element={<WrappedCCSSPage />} />}
            />
            <Route
              path="/ilearn"
              element={
                <ProtectedRoute element={<WrappedILEARNModulesPage />} />
              }
            />
            <Route
              path="/ilearn/ab"
              element={
                <ProtectedRoute element={<WrappedILEARNModule1Page />} />
              }
            />
            <Route
              path="/ilearn/ps"
              element={
                <ProtectedRoute element={<WrappedILEARNModule2Page />} />
              }
            />
            <Route
              path="/ilearn/cci"
              element={
                <ProtectedRoute element={<WrappedILEARNModule3Page />} />
              }
            />
            <Route
              path="/ilearn/cpi"
              element={
                <ProtectedRoute element={<WrappedILEARNModule4Page />} />
              }
            />
            <Route
              path="/ilearn/cs"
              element={
                <ProtectedRoute element={<WrappedILEARNModule5Page />} />
              }
            />
            <Route
              path="/ilearn/ip"
              element={
                <ProtectedRoute element={<WrappedILEARNModule6Page />} />
              }
            />
            <Route
              path="/ilearn/eo"
              element={
                <ProtectedRoute element={<WrappedILEARNModule7Page />} />
              }
            />
            <Route
              path="/myinfo/certificate"
              element={<ProtectedRoute element={<WrappedCertificatePage />} />}
            />
            <Route
              path="/admincentral/isafeadmins/dashboard"
              element={
                <ProtectedRoute element={<WrappedISAFEAdminDashboardPage />} />
              }
            />
            <Route
              path="/admincentral/dashboard"
              element={
                <ProtectedRoute element={<WrappedISAFEAdminDashboardPage />} />
              }
            />
            <Route
              path="/admincentral/announcements/view/:id"
              element={
                <ProtectedRoute element={<WrappedAdminAnnouncementsPage />} />
              }
            />

            <Route
              path="/admincentral/subscriptions/search"
              element={
                <ProtectedRoute element={<WrappedAdminSubscriptionsPage />} />
              }
            />

            <Route
              path="/admincentral/subscriptions"
              element={
                <ProtectedRoute element={<WrappedAdminSubscriptionsPage />} />
              }
            />
            <Route
              path="/admincentral/isafeadmins"
              element={<ProtectedRoute element={<WrappedAdminsPage />} />}
            />
            <Route
              path="/admincentral/isafeadmins"
              element={<ProtectedRoute element={<WrappedAdminsPage />} />}
            />
            <Route
              path="/admincentral/orgs/search"
              element={<ProtectedRoute element={<WrappedDirectoryPage />} />}
            />
            <Route
              path="/admincentral/other"
              element={<ProtectedRoute element={<WrappedOtherPage />} />}
            />
            <Route
              path="/admincentral/teachers"
              element={<ProtectedRoute element={<WrappedTeachersPage />} />}
            />
            <Route
              path="/admincentral/admins"
              element={
                <ProtectedRoute element={<WrappedSubscriptionAdminPage />} />
              }
            />
            <Route
              path="/admincentral/faq"
              element={<ProtectedRoute element={<WrappedUserGuidePage />} />}
            />
            <Route
              path="/admincentral/resources"
              element={<ProtectedRoute element={<WrappedResourcesPage />} />}
            />
            <Route
              path="/admincentral/reports"
              element={<ProtectedRoute element={<WrappedReportsPage />} />}
            />
            {/* Redirect unmatched routes */}
            <Route
              path="*"
              element={
                signedIn ? (
                  <Navigate to="/myinfo" />
                ) : (
                  <Navigate to={`/user/login${location.search}`} />
                )
              }
            />
          </Routes>
        </RoutesWrapper>
        <Footer colors={colors} />
      </ContentContainer>
      <EdBrowserChatbot colors={colors} />
    </AppContainer>
  );
};

export default App;
